import React, { createContext, useContext } from "react";
import { Navigate, Outlet } from "react-router-dom";

const AuthRoutingContext = createContext(null);

/**
 * Holds token + role for route guards (hydrated after localStorage/JWT read).
 */
export function AuthRoutingProvider({ value, children }) {
  return (
    <AuthRoutingContext.Provider value={value}>
      {children}
    </AuthRoutingContext.Provider>
  );
}

export function useAuthRouting() {
  const ctx = useContext(AuthRoutingContext);
  if (ctx == null) {
    throw new Error("useAuthRouting must be used within AuthRoutingProvider");
  }
  return ctx;
}

/**
 * Public routes (e.g. /login). Authenticated users are redirected by role.
 */
export function PublicRoute() {
  const { token, role } = useAuthRouting();
  if (token) {
    if (role === "admin") {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
}

/**
 * Protected routes. Pass allowedRoles e.g. ['admin'] or ['employee'].
 * Wrong role → redirect to the correct home; no token → /login.
 */
export function ProtectedRoute({ allowedRoles }) {
  const { token, role } = useAuthRouting();

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && allowedRoles.length > 0) {
    const ok = allowedRoles.includes(role);
    if (!ok) {
      if (role === "admin") {
        return <Navigate to="/admin" replace />;
      }
      return <Navigate to="/" replace />;
    }
  }

  return <Outlet />;
}
